// src/redux/usersSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginUser,
  getCurrentUser,
  signupUser,
  getAllUsers,
  modifyUser,
  deleteUser,
  changePassword,
  getUserById,
  blockUser,
  getAssignedUsers,
  sendVerificationCode as sendVerificationCodeAPI,
  verifyAccount as verifyAccountAPI,
  sendResetPasswordLink as sendResetPasswordLinkAPI,
  resetPassword as resetPasswordAPI,
  getProfileById,
  logoutUser // Import logoutUser API
} from "../services/apiService";

// Helper function to compute token expiration time in milliseconds
const getTokenExpirationTime = (token) => {
  if (!token) return 0;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expTime = payload.exp * 1000 - Date.now();
    return expTime > 0 ? expTime : 0;
  } catch (error) {
    console.error("Error parsing token expiration:", error);
    return 0;
  }
};

// Async thunk for user login
export const userLogin = createAsyncThunk(
  "users/login",
  async (credentials, { rejectWithValue }) => {
    try {
      if (!credentials.email.endsWith("@dotmedusa.com")) {
        return rejectWithValue("Accès refusé. Seuls les e-mails @dotmedusa.com sont autorisés.");
      }
      const loginData = await loginUser(credentials);
      localStorage.setItem("token", loginData.token);

      const user = await getCurrentUser();

      if (user?.status === "isBlocked") {
        localStorage.removeItem("token");
        return rejectWithValue("Vous êtes bloqué.");
      }
      return { ...loginData, currentUser: user };
    } catch (error) {
      localStorage.removeItem("token");
      const errorMessage =
        error.response &&
        error.response.data &&
        error.response.data.messages &&
        error.response.data.messages.value
          ? error.response.data.messages.value
          : "Adresse Email ou Mot de passe incorrect.";
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for user signup
export const userSignup = createAsyncThunk(
  "users/signup",
  async (
    { email, password, username, fullName, address, phone, birthDate, companyId, roles, captchaVerification },
    { rejectWithValue }
  ) => {
    try {
      if (password.includes(email) || password.includes(username) || (fullName && password.includes(fullName))) {
        return rejectWithValue({ error: "Le mot de passe ne doit pas contenir d'e-mail, de nom d'utilisateur ou de nom complet" });
      }
      const data = await signupUser({
        email,
        password,
        username,
        fullName,
        address,
        phone,
        birthDate,
        companyId,
        roles,
        captchaVerification
      });
      return data;
    } catch (error) {
      if (error.response.data === 'Username already exists.') {
        return rejectWithValue({ error: "Le nom d'utilisateur existe déjà." });
      }
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching the current user
export const fetchCurrentUser = createAsyncThunk(
  "users/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getCurrentUser();
      return data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/';
      }
      return rejectWithValue(
        error.response?.data ||
          "Une erreur s'est produite lors de la récupération de l'utilisateur actuel."
      );
    }
  }
);

// Async thunk for fetching all users
export const fetchAllUsers = createAsyncThunk(
  "users/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAllUsers();
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération des utilisateurs.");
    }
  }
);

// Async thunk for fetching all users for dropdown
export const fetchAllUsersForDropdown = createAsyncThunk(
  "users/fetchAllUsersForDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAllUsers();
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération des utilisateurs.");
    }
  }
);

// Async thunk for updating a user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, data, profilePicture }, { rejectWithValue, dispatch, getState }) => {
    try {
      const updatedUser = await modifyUser(id, data, profilePicture);
      const { currentUser } = getState().users;
      if (currentUser.roles.includes("ROLE_SUPER_ADMIN")) {
        dispatch(fetchAllUsers());
      } else if (currentUser.roles.includes("ROLE_ADMIN")) {
        dispatch(fetchAssignedUsers());
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la mise à jour de l'utilisateur.");
    }
  }
);

// Async thunk for deleting a user
export const removeUser = createAsyncThunk(
  "users/removeUser",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      await deleteUser(id);
      const { currentUser } = getState().users;
      if (currentUser.roles.includes("ROLE_SUPER_ADMIN")) {
        dispatch(fetchAllUsers());
      } else if (currentUser.roles.includes("ROLE_ADMIN")) {
        dispatch(fetchAssignedUsers());
      }
      return id;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la suppression de l'utilisateur.");
    }
  }
);

// Async thunk for changing user password
export const changeUserPassword = createAsyncThunk(
  "users/changePassword",
  async ({ id, oldPassword, newPassword }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { currentUser } = getState().users;
      const data = await changePassword(id, oldPassword, newPassword);
      if (currentUser.roles.includes("ROLE_SUPER_ADMIN")) {
        dispatch(fetchAllUsers());
      } else if (currentUser.roles.includes("ROLE_ADMIN")) {
        dispatch(fetchAssignedUsers());
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching a user by ID
export const fetchUserById = createAsyncThunk(
  "users/fetchUserById",
  async (id, { rejectWithValue }) => {
    try {
      const data = await getUserById(id);
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération de l'utilisateur.");
    }
  }
);

// Async thunk for blocking/unblocking a user
export const toggleBlockUser = createAsyncThunk(
  "users/toggleBlockUser",
  async ({ id, isBlocked }, { rejectWithValue, dispatch, getState }) => {
    try {
      const updatedUser = await blockUser(id, isBlocked);
      const { currentUser } = getState().users;
      if (currentUser.roles.includes("ROLE_SUPER_ADMIN")) {
        dispatch(fetchAllUsers());
      } else if (currentUser.roles.includes("ROLE_ADMIN")) {
        dispatch(fetchAssignedUsers());
      }
      return updatedUser;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors du blocage/déblocage de l'utilisateur");
    }
  }
);

// Async thunk for fetching assigned users
export const fetchAssignedUsers = createAsyncThunk(
  "users/fetchAssignedUsers",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAssignedUsers();
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération des collaborateurs.");
    }
  }
);

// Async thunk for sending verification code
export const sendVerificationCode = createAsyncThunk(
  "users/sendVerificationCode",
  async (_, { rejectWithValue }) => {
    try {
      const data = await sendVerificationCodeAPI();
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de l'envoi du code de vérification.");
    }
  }
);

// Async thunk for verifying account
export const verifyAccount = createAsyncThunk(
  "users/verifyAccount",
  async (code, { rejectWithValue }) => {
    try {
      const data = await verifyAccountAPI(code);
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la vérification du compte.");
    }
  }
);

// Async thunk for sending reset password link with captcha verification
export const sendResetPasswordLink = createAsyncThunk(
  "users/sendResetPasswordLink",
  async ({ email, captchaVerification }, { rejectWithValue }) => {
    try {
      const data = await sendResetPasswordLinkAPI({ email, captchaVerification });
      return data;
    } catch (error) {
      return rejectWithValue("Échec de l'envoi du lien de réinitialisation.");
    }
  }
);

// Async thunk for resetting password
export const resetPassword = createAsyncThunk(
  "users/resetPassword",
  async ({ token, newPassword, confirmNewPassword }, { rejectWithValue }) => {
    try {
      const data = await resetPasswordAPI(token, newPassword, confirmNewPassword);
      return data;
    } catch (error) {
      return rejectWithValue("Échec de la réinitialisation du mot de passe.");
    }
  }
);

// Async thunk for fetching a profile by ID
export const fetchProfileById = createAsyncThunk(
  "users/fetchProfileById",
  async (id, { rejectWithValue }) => {
    try {
      const data = await getProfileById(id);
      return data;
    } catch (error) {
      return rejectWithValue("Une erreur s'est produite lors de la récupération du profil.");
    }
  }
);

// New async thunk for logging out the user
export const logoutUserAsync = createAsyncThunk(
  "users/logout",
  async (_, { rejectWithValue }) => {
    try {
      await logoutUser(); // Wait for the API logout to complete
      return true;
    } catch (error) {
      return rejectWithValue("Erreur lors de la déconnexion.");
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState: {
    token: localStorage.getItem("token") || null,
    loading: false,
    error: null,
    currentUser: null,
    signupSuccess: null,
    users: [],
    selectedUser: null,
    assignedUsers: [],
    allUsers: [],
    isSignUpFromManagement: false,
    verificationCodeSent: false,
    resetLinkSent: false,
    resetTokenVerified: false,
  },
  reducers: {
    // Optionally, you can keep a synchronous logout if needed for local cases.
    // But using the async thunk is recommended.
    clearSignupSuccess(state) {
      state.signupSuccess = null;
    },
    // Synchronous token expiration logout reducer
    tokenExpirationLogout(state) {
      state.token = null;
      state.currentUser = null;
      localStorage.removeItem("token");
      window.location.href = "/";
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle user login actions
      .addCase(userLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.currentUser = action.payload.currentUser;
        localStorage.setItem("token", action.payload.token);

        // Set a logout timer based on the token's expiration time
        const expirationTime = getTokenExpirationTime(action.payload.token);
        if (expirationTime) {
          setTimeout(() => {
            localStorage.removeItem("token");
            window.location.href = "/";
          }, expirationTime);
        }
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle user signup actions
      .addCase(userSignup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.signupSuccess = null;
      })
      .addCase(userSignup.fulfilled, (state) => {
        state.loading = false;
        state.signupSuccess = "Compte créé avec succès, vous allez être redirigé vers la page de connexion !";
      })
      .addCase(userSignup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || action.payload || "Une erreur s'est produite lors de l'inscription.";
      })
      // Handle fetch current user actions
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch all users actions
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch all users for dropdown actions
      .addCase(fetchAllUsersForDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsersForDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsers = action.payload;
      })
      .addCase(fetchAllUsersForDropdown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle update user actions
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle remove user actions
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle change password actions
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || action.payload || "Une erreur s'est produite lors de la modification du mot de passe.";
      })
      // Handle fetch user by ID actions
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle block/unblock user actions
      .addCase(toggleBlockUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleBlockUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(toggleBlockUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch assigned users actions
      .addCase(fetchAssignedUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignedUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.assignedUsers = action.payload;
      })
      .addCase(fetchAssignedUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle send verification code actions
      .addCase(sendVerificationCode.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.verificationCodeSent = false;
      })
      .addCase(sendVerificationCode.fulfilled, (state) => {
        state.loading = false;
        state.verificationCodeSent = true;
      })
      .addCase(sendVerificationCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle verify account actions
      .addCase(verifyAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
      })
      .addCase(verifyAccount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle send reset password link actions
      .addCase(sendResetPasswordLink.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetLinkSent = false;
      })
      .addCase(sendResetPasswordLink.fulfilled, (state) => {
        state.loading = false;
        state.resetLinkSent = true;
      })
      .addCase(sendResetPasswordLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle reset password actions
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.resetTokenVerified = false;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetTokenVerified = true;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch profile by ID actions
      .addCase(fetchProfileById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProfile = action.payload;
      })
      .addCase(fetchProfileById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle logout actions with the async thunk
      .addCase(logoutUserAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUserAsync.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.currentUser = null;
        localStorage.removeItem("token");
        // Redirect after successful logout
        window.location.href = "/";
      })
      .addCase(logoutUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSignupSuccess, tokenExpirationLogout } = usersSlice.actions;
export default usersSlice.reducer;
